import { Box, CircularProgress, Skeleton, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';

/** Number of placeholder rows in the skeleton to mimic transcription entries. */
const SKELETON_ROW_COUNT = 3;

/** Width ratio for speaker column (narrow) vs text column (wide), matching the table layout. */
const SPEAKER_COLUMN_WIDTH = '18%';

/**
 * Skeleton loader that mirrors the transcription table UI: card with header (Speaker Label | Texto)
 * and content rows with narrow speaker cell and variable-length text cell.
 */
export function TranscriptionSkeleton({ showMessage = true }: { showMessage?: boolean }) {
  return (
    <Box sx={{ marginTop: '1rem' }}>
      <Box
        sx={{
          position: 'relative',
          borderRadius: 2,
          boxShadow: 1,
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        {showMessage && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              <FormattedMessage
                id="audit.diarization.processing"
                defaultMessage="Procesando transcripción"
              />
            </Typography>
          </Box>
        )}
        {/* Header row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            py: 1.5,
          }}
        >
          <Skeleton
            variant="text"
            width={100}
            height={20}
            sx={{ flexShrink: 0, flex: `0 0 ${SPEAKER_COLUMN_WIDTH}` }}
          />
          <Skeleton variant="text" width={60} height={20} sx={{ flex: 1, maxWidth: 120 }} />
        </Box>
        {/* Content rows */}
        {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              borderBottom: 1,
              borderColor: 'divider',
              px: 2,
              py: 1.5,
              '&:last-of-type': { borderBottom: 0 },
            }}
          >
            <Skeleton
              variant="text"
              width={72}
              height={20}
              sx={{ flexShrink: 0, flex: `0 0 ${SPEAKER_COLUMN_WIDTH}` }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton
                variant="text"
                width={i % 3 === 0 ? '100%' : i % 3 === 1 ? '75%' : '55%'}
                height={20}
                sx={{ display: 'block' }}
              />
              {i % 2 === 0 && i < 4 && (
                <Skeleton
                  variant="text"
                  width="90%"
                  height={20}
                  sx={{ display: 'block', mt: 0.5 }}
                />
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
